import React, { useEffect, useState } from "react";
import Selector from "components/Selector/Selector";
import Search from "components/Inputs/Search/Search";
import CustomTable from "components/CustomTable/CustomTable";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import { SizeType } from "antd/es/config-provider/SizeContext";
import editIcon from "../../../images/_edit.svg";
import minusIcon from "../../../images/minus.svg";
import documentIcon from "../../../images/document.png";
import eyeIcon from "../../../images/eye.png";
import deleteIcon from "../../../images/trashpink.svg";
import { PlusOutlined } from "assets/icons";
import { useDispatch, useSelector } from "react-redux";
import ConfirmPopUp from "components/PopUp/ConfirmPopUp/ConfirmPopUp";
import { Link, useNavigate } from "react-router-dom";
import { t } from "i18next";
import useMessage from "hooks/useMessage";
import { DateRangeSelector } from "components/DateTimeSelectors/DateRangeSelector/DateRangeSelector";
import { API_ENDPOINTS, ROUTES } from "utils/constants";
import { PAGE_OPTIONS, platformOptions } from "utils/constants/selectorOptions";
import centralApi from "services/centralApi";
import { getToken } from "redux/features/app/authTokenSlice";
import AccessControl from "components/HOC/AccessControl/AccessControl";
import { roleCheckTypes } from "utils/constants/constants";
import PageLayout from "layout/PageLayout/PageLayout";
import CustomBreadCrumb from "components/CustomBreadCrumb/CustomBreadCrumb";
import { PlatformTypeEnum } from "utils/enums";
import "./FunnelsTable.css";

type propTypes = {
  pageSize?: number;
  tableSize?: SizeType;
};
const FunnelsTable: React.FC<propTypes> = ({ tableSize }) => {
  const navigate = useNavigate();
  const loginUser = useSelector((state: any) => state.loginUser);
  const { appId } = useSelector((state: any) => state.activeApp);
  const [funnelData, setFunnelData] = useState({raw:[],filtered:[],platform:PlatformTypeEnum.ALL,loading:true});
  const { showError, showSuccess } = useMessage();
  const [pageData, setPageData] = useState({
    pageSize: PAGE_OPTIONS.DEFAULT_VALUE,
    simple: PAGE_OPTIONS.PAGINATION.simple,
  });
  const [dateRange, setDateRange] = useState<any>({});
  const dispatch: any = useDispatch();

  //get the funnels data from api and set the state
  async function getEventFunnelsData() {
    try {
      const apiKey = loginUser.data.api_key;
      if (apiKey) {
        const url: string = API_ENDPOINTS.GET_EVENT_FUNNELS_URL;
        const payload = {
          d:dateRange,
          app_id: appId,
          api_key:apiKey,
        };
        setFunnelData((prevState)=>({...prevState,loading:true}));
        const res = await centralApi("GET", url, null, payload);
        setFunnelData((prevState)=>({...prevState,raw:res,filtered:[],loading:false}));
        filterData();
      }
    } catch (error) {
      setFunnelData((prevState)=>({...prevState,loading:false}));
    }
  }

  //on  popup confirm
  const funnelDeleteConfirm = async (funnelId: string) => {
    try {
      const url: string = API_ENDPOINTS.DELETE_FUNNEL_URL; 
      const payload = {
        app_id: appId,
        id: funnelId,
        api_key: loginUser.data.api_key,
      };
      const response = await centralApi("GET", url, null, payload);

      if(response){
        showSuccess(t("funneldeletedSuccessfullyLabel"));
        getEventFunnelsData(); 
      }
      
    } catch (error) {
      showError(t("somethingWrongLabel"));
    }
  };
  const funnelCopyConfirm = async (funnelId: string) => {
    try {
      const url: string = API_ENDPOINTS.COPY_FUNNEL_URL;
      const payload = {
        id:funnelId,
        app_id :appId,
        api_key:loginUser.data.api_key,
      };
      const response = await centralApi("GET", url, null, payload);
      if(response){
        showSuccess(t("copiedMsg"));
        getEventFunnelsData();
        dispatch(getToken())
      }     
    } catch (error) {
      showError(t("somethingWrongLabel"));
    }
  };
  useEffect(() => {
    const apiKey = loginUser.data.api_key;
    if (apiKey && appId) {
      getEventFunnelsData();
    }
  }, [loginUser]);
  const filterData = (search = "") => {
    const searchRegex = new RegExp(search, "i");
    setFunnelData((prevState)=>{
     const filteredData= prevState.raw?.filter((fun: any) => {
        // Check if the date range is specified and valid
        const isWithinDateRange =
          (!dateRange?.startDate_C ||
            fun.modifiedOn >= dateRange.startDate_C) &&
          (!dateRange?.endDate_C || fun.modifiedOn <= dateRange.endDate_C);

        // Check if the search term matches
        const isSearchMatch = searchRegex.test(fun.name);

        const platformMatch=funnelData.platform===PlatformTypeEnum.ALL || (fun?.platform || fun?.p || [])?.some((platform:string) => typeof platform=="string" && platform?.toLowerCase() === funnelData.platform?.toLowerCase())

        // Return true if both conditions are met
        return isWithinDateRange && isSearchMatch && platformMatch;
      })
      return {...prevState,filtered:filteredData}
    });
  };
  useEffect(() => {
    if (funnelData.raw?.length) {
      filterData();
    }
  }, [dateRange, funnelData.raw,funnelData.platform]);
  const columns: any = [
    {
      title: t("funnelNameLabel"),
      dataIndex: "name",
    },
    {
      title: t("funnelDescriptionLabel"),
      dataIndex: "description",
    },
    {
      title: t("actionLabel"),
      dataIndex: "_id",
      width: 180,
      className: "item-align",

      render: (id: string) => {
       
        return (
          <span className="d-flex justify-content-between campaign-action-btns">
          <AccessControl accessControl={{role:[roleCheckTypes.Checker ,roleCheckTypes.Maker]}}> 
            <ConfirmPopUp
              title={t("copyFunnelMsg")}
              onConfirm={funnelCopyConfirm}
              id={id}
              icon={documentIcon}
              description={t("areYouSureMsg")}
              placement="leftTop"
            >
              <PrimaryButton title={t("copyLabel")} className="action-btn mx-1">
                <img
                  src={documentIcon}
                  alt={"document_icon"}
                ></img>
              </PrimaryButton>
            </ConfirmPopUp>
            <PrimaryButton
              title={t("editLabel")}
              className="action-btn mx-1"
              onClick={() => navigate("create", { state: { id } })}
            >
              <img src={editIcon} alt={"edit_icon"}></img>
            </PrimaryButton>
            <ConfirmPopUp
              title={t("deleteFunnelMsg")}
              onConfirm={funnelDeleteConfirm}
              id={id}
              icon={deleteIcon}
              placement="leftTop"
            >
              <PrimaryButton
                title={t("deleteLabel")}
                className="action-btn mx-1"
              >
                <img src={minusIcon} alt={"minus_icon"}></img>
              </PrimaryButton>
            </ConfirmPopUp>
             </AccessControl>
            <Link to={`${ROUTES.DASHBOARD_FUNNEL_DETAIL.replace(":id", id)}`}>
              <PrimaryButton title={t("viewLabel")} className="action-btn mx-1">
                <img src={eyeIcon} alt={"eye_icon"}></img>
              </PrimaryButton>
            </Link>
          </span>
        );
      },
    },
  ];

  return (
  <div className="funnels-table-main-wrapper">
    <PageLayout
      title={`${t("funnelsLabel")}`}
      topLeftItem={
        <CustomBreadCrumb
            items={[
              {
                title: <Link to={ROUTES.DASHBOARD_FUNNELS}>{t("funnelsDataLabel")}</Link>,
              }
            ]}
          />}
      topRightItem={
        <div className="d-flex justify-content-end gap-1">
          <div>
          <strong>{t("platformLabel")} </strong>{" "}
          <Selector className="input-size-sm"  value={funnelData.platform} onChange={(platform)=>setFunnelData((prevState)=>({...prevState,platform}))} options={platformOptions(true)}/>
        </div>
        <div>
          <strong>{t("periodLabel")} </strong>{" "}
          <DateRangeSelector
            onDateChange={(dateRangeData: any) => {
              setDateRange(dateRangeData);
            }}
            disableRange={{ range: 1, rangeType: "year" }}
            disableType="year"
            initialValue={{}}
            numDaysToShow={30} // Example value
          />
        </div>
        </div>
      }
    >
      <div className="w-100 d-flex justify-content-between mb-3 ">
        <div className="d-flex gap-2">
        <Search
          placeholder={`${t("searchLabel")}`}
          onChange={(e) => {
            filterData(e.target.value);
          }}
          size="middle"
          className="input-size-lg"
        />
        <Selector
          onChange={(value) => {
            setPageData({ ...pageData, pageSize: value });
          }}
          className="funnels-page-selector"
          size="middle"
          defaultValue={PAGE_OPTIONS.DEFAULT_VALUE}
          options={PAGE_OPTIONS.DATA}
        />
         </div>

        <AccessControl
          accessControl={{
            role: [roleCheckTypes.Checker, roleCheckTypes.Maker],
          }}
        >
          <div className="">
            <PrimaryButton
              type="primary"
              onClick={() => {
                navigate("create");
              }}
            >
              <PlusOutlined />
              <span className="mx-1">{t("funnelLabel")}</span>
            </PrimaryButton>
          </div>
        </AccessControl>
      </div>

      <CustomTable
        columns={columns}
        dataSource={funnelData.filtered?.map((fun: any) => ({
          ...fun,
          key: fun._id,
        }))}
        totalEntries={funnelData.raw?.length}
        size={tableSize || "small"}
        loading={funnelData.loading}
        pagination={pageData}
        scroll={{ x: 500 }}
      />
    </PageLayout>
  </div>
  );
};

export default FunnelsTable;
